import React from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import { useI18n } from "../../i18n";

// Product categories that fall under the CRA — collage of digital-element
// products (software, IoT, hardware, networking).
const COLLAGE = {
  software: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=800&q=70",
  iot: "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=800&q=70",
  hardware: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=70",
  network: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=800&q=70",
};

export const Manifesto = () => {
  const { t } = useI18n();
  return (
    <section id="approach" data-testid="manifesto-section" className="relative py-24 md:py-36 bg-[#080F1D] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Sticky heading */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6] mb-6">
              {t.manifesto.overline}
            </div>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.03] text-white">
              {t.manifesto.title}
            </h2>
            {t.manifesto.intro && (
              <p className="mt-6 text-base md:text-lg text-gray-400 leading-relaxed max-w-md">
                {t.manifesto.intro}
              </p>
            )}
            <div className="mt-10 hidden lg:block">
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-3">
                {t.manifesto.collageTitle}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {t.manifesto.collage.map((c) => (
                  <div
                    key={c.k}
                    data-testid={`cra-collage-${c.k}`}
                    className="group relative aspect-[4/3] overflow-hidden rounded-sm bg-[#030712] ring-1 ring-inset ring-white/10"
                  >
                    <img
                      src={COLLAGE[c.k]}
                      alt={c.label}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 [filter:grayscale(1)_contrast(1.1)_brightness(0.75)]"
                    />
                    {/* Blue duotone treatment */}
                    <div className="absolute inset-0 bg-[#0052FF] mix-blend-color opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#030712]/70 via-transparent to-[#0052FF]/20" />
                    {/* Label */}
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-[#030712] via-[#030712]/70 to-transparent">
                      <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[#3B82F6]">
                        {c.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="lg:col-span-7">
          {t.manifesto.steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 1, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`manifesto-step-${i}`}
              className="group py-10 md:py-12 border-t border-white/10 last:border-b"
            >
              <div className="flex items-baseline gap-6 md:gap-10">
                <div aria-hidden="true" className="font-display text-4xl md:text-5xl font-extrabold text-white/15 group-hover:text-[#3B82F6] transition-colors duration-300 shrink-0">
                  {s.n}
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                    {s.t}
                  </h3>
                  {s.lead && (
                    <p className="mt-3 text-lg text-white/80 font-medium leading-snug max-w-2xl">
                      {s.lead}
                    </p>
                  )}
                  <p className="mt-4 text-base text-gray-400 leading-relaxed max-w-2xl">
                    {s.d}
                  </p>
                  {s.outcome && (
                    <div className="mt-5 flex items-start gap-3 max-w-2xl">
                      <div className="mt-2 h-px w-6 bg-[#3B82F6] shrink-0" />
                      <p className="text-sm md:text-base text-[#3B82F6] font-medium leading-snug">
                        {s.outcome}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
